import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Divider,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DataTable from "examples/Tables/DataTable";
import MDAlert from "components/Alert";
import FindInPageOutlinedIcon from "@mui/icons-material/FindInPageOutlined";
import QueryBuilderIcon from "@mui/icons-material/QueryBuilder";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MDBadge from "components/MDBadge";
import CloseIcon from "@mui/icons-material/Close";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import QRScanner from "components/QRScanner/QRScanner";
import "./verifyByDocument.css";

export default function VerifyByDocument({
  handleFieldChange,
  handleSubmit,
  data,
  pageIndexAndPageSize,
  handleSearch,
  isLoading,
  isAuthenticated = false,
}) {
  const [hasSearched, setHasSearched] = useState(false);
  // cámara / escáner ahora se maneja en el componente reutilizable QRScanner
  const [isAlert, setIsAlert] = useState(false);
  const [typeAlert, setTypeAlert] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const streamRef = useRef(null);
  const navigate = useNavigate();
  // Nota: usamos `navigate` de react-router para movernos internamente
  // cuando el QR contiene una ruta o una URL del mismo sistema.

  const handleSearchSubmit = () => {
    setHasSearched(true);
    handleSubmit();
  };

  useEffect(() => {
    return () => {
      // cleanup on unmount (no-op aquí, el QRScanner maneja su propio cleanup)
    };
  }, []);

  const handleAlert = (type, message) => {
    setTypeAlert(type);
    setAlertMessage(message || "");
    setIsAlert(true);
  };
  // Handler que recibe el texto escaneado por el componente QRScanner
  const handleQRDetected = (scannedRaw) => {
    const scanned = (scannedRaw || "").trim();
    console.debug("[VerifyByDocument] QR scanned:", scanned);

    // Solo navegaremos automáticamente para rutas /verify/ (o URLs que apunten a /verify/)
    const verifyMatch = scanned.match(/(\/verify\/[^\/]+\/(?:true|false)\/?)/i);
    if (verifyMatch && verifyMatch[1]) {
      navigate(verifyMatch[1]);
      return;
    }

    // Si es una URL completa que contiene '/verify/' en su path, extraer y navegar
    if (/^https?:\/\//i.test(scanned)) {
      try {
        const parsed = new URL(scanned);
        if (parsed.pathname && /\/verify\//i.test(parsed.pathname)) {
          const path = parsed.pathname + parsed.search + parsed.hash;
          navigate(path);
          return;
        }
        // si es una URL que no apunta a /verify, considerarla inválida
        handleAlert("fail", "QR no es válido");
        return;
      } catch (e) {
        // si no se pudo parsear la URL, considerarlo inválido
        handleAlert("fail", "QR no es válido");
        return;
      }
    }

    // Si es una ruta absoluta dentro de la app, permitir solo si contiene /verify/
    if (scanned.startsWith("/")) {
      if (/\/verify\//i.test(scanned)) {
        navigate(scanned);
        return;
      }
      handleAlert("fail", "QR no es válido");
      return;
    }

    // Fallback: si no es una ruta/URL del sistema, mostramos una alerta de "QR no es válido"
    // En caso de que sea solo un número o texto que deba buscarse, seguimos con la búsqueda.
    const looksLikeId = /^[0-9A-Za-z\-_.@]{3,}$/.test(scanned);
    if (looksLikeId) {
      handleFieldChange("name", scanned);
      setHasSearched(true);
      if (typeof handleSubmit === "function") handleSubmit();
      return;
    }

    // No coincide con rutas ni parece un identificador útil: mostrar alerta
    handleAlert("fail", "QR no es válido");
  };

  const getLocalDate = (dateString) => {
    const date = new Date(dateString);

    const options = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: true, // Formato de 12 horas
    };

    return date.toLocaleString("es-CO", options); // "es-CO" es el código de lenguaje para Colombia
  };
  return (
    <>
      <Typography variant="h3" component="h1" gutterBottom>
        ¡Bienvenido a la página de verificación de carnets de la IU Digital!
      </Typography>
      <MDAlert
        isAlert={isAlert}
        setIsAlert={setIsAlert}
        typeAlert={typeAlert}
        message={alertMessage}
      />
      <Typography variant="subtitle1" color="textSecondary" paragraph>
        Digita el número de documento:
      </Typography>
      {/* Styles moved to ./verifyByDocument.css */}
      {/* Mantener el input en su lugar; icono posicionado absoluto a la derecha del input (no mover el flow) */}
      <div className="input-with-scanner">
        <MDInput
          style={{ marginTop: "5px" }}
          label=""
          onChange={(e) => handleFieldChange("name", e.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault(); // Previene comportamiento por defecto
              handleSearchSubmit(); // Llamar la función sin pasar
            }
          }}
        />

        <div className="qr-scanner-position">
          <QRScanner
            onDetected={handleQRDetected}
            onShowAlert={handleAlert}
            iconOnly={true}
            tooltip="Escanear QR"
            icon={
              <div aria-hidden="true" className="qr-icon">
                {/* Icono de Material UI */}
                <CameraAltIcon style={{ color: "#1976d2" }} />
              </div>
            }
          />
        </div>
      </div>
      <Divider variant="middle" />
      {/* El componente QRScanner maneja el diálogo/cámara ahora (componente reutilizable) */}
      <MDButton onClick={handleSearchSubmit} type="submit" color="success">
        Consultar
      </MDButton>
      <div
        style={{
          width: "auto",
          margin: "2rem",
        }}
      >
        {isLoading ? (
          <div>Cargando...</div>
        ) : data && data.length > 0 ? (
          <DataTable
            table={{
              columns: [
                { Header: "Estado", accessor: "status", width: "12%" },
                { Header: "fuentes", accessor: "data_source_name", width: "12%" },
                { Header: "Fecha de Creación", accessor: "created_at", width: "12%" },
                { Header: "Fecha de Expiración", accessor: "expires_in", width: "12%" },
                { Header: "Tipo de Carnet", accessor: "Tipo_carnet", width: "12%" },
                { Header: "Ingreso", accessor: "joined", width: "12%" },
                { Header: "Acciones", accessor: "view", width: "12%", disableSortBy: true },
              ],
              rows: data?.map((item) => ({
                status:
                  item.status === "Activo" ? (
                    <MDBadge badgeContent="Activo" size="xs" container color="success" />
                  ) : (
                    <MDBadge badgeContent={item.status} size="xs" container color="error" />
                  ),
                data_source_name: item.data_source_name,
                created_at: getLocalDate(item.created),
                expires_in: getLocalDate(item.expires_in),
                Tipo_carnet: item.Tipo_carnet,

                joined:
                  item.Tipo_carnet === "Invitado" || item.Tipo_carnet === "Visitante" ? (
                    item.joined == 1 ? (
                      <CheckCircleIcon style={{ color: "green" }} />
                    ) : (
                      <QueryBuilderIcon style={{ color: "red" }} />
                    )
                  ) : (
                    "N/A"
                  ),
                view: (
                  <>
                    <a style={{ cursor: "pointer" }} href={`/verify/${item.id}/false`}>
                      <FindInPageOutlinedIcon
                        style={{
                          fontSize: "200px",
                          width: "24px",
                          height: "24px",
                          backgroundImage: "#000000",
                        }}
                      ></FindInPageOutlinedIcon>
                    </a>
                  </>
                ),
              })),
            }}
            pageIndexAndPageSize={pageIndexAndPageSize}
            canSearch={false}
            isSorted={true}
            entriesPerPage={false}
            showTotalEntries={false}
            checkboxSelection={false}
          />
        ) : (
          // Mostrar el mensaje cuando no hay resultados
          <div>
            {hasSearched ? "No hay resultados" : "Realiza una búsqueda para ver los resultados"}
          </div>
        )}
      </div>
    </>
  );
}
